import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import MuiCard from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import CircularProgress from "@mui/material/CircularProgress";

import { styled } from "@mui/material/styles";

import { Visibility, VisibilityOff, EmailRounded, LockRounded, LoginRounded } from "@mui/icons-material";
import { IconButton, InputAdornment } from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from '../../../../Configs/axiosInstance';
import { MAIN_URL } from "../../../../Configs/Urls";
import LogoImage from '../../../../Assets/images/Logos/logo.JPG';

const Card = styled(MuiCard)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignSelf: "center",
  width: "100%",
  padding: theme.spacing(4.5),
  gap: theme.spacing(2.5),
  borderRadius: 16,
  boxShadow:
    "hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px",
  [theme.breakpoints.up("sm")]: {
    width: "450px",
  },
  ...theme.applyStyles("dark", {
    boxShadow:
      "hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px",
  }),
}));

export default function SignInCard() {
  const [emailError, setEmailError] = React.useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = React.useState("");
  const [passwordError, setPasswordError] = React.useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  const [showPassword, setShowPassword] = React.useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  let navigate = useNavigate();

  const validateInputs = () => {
    const email = document.getElementById("email").value;
    const password = document.getElementById("password").value;

    let isValid = true;

    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setEmailError(true);
      setEmailErrorMessage("Please enter a valid email address.");
      isValid = false;
    } else {
      setEmailError(false);
      setEmailErrorMessage("");
    }

    if (!password || password.length < 6) {
      setPasswordError(true);
      setPasswordErrorMessage("Password must be at least 6 characters long.");
      isValid = false;
    } else {
      setPasswordError(false);
      setPasswordErrorMessage("");
    }

    return isValid;
  };


  const handleSubmit = async (event) => {
    event.preventDefault();

    // Validate inputs before making API call
    if (!validateInputs()) {
      return;
    }

    const data = new FormData(event.currentTarget);
    const email = data.get("email");
    const password = data.get("password");

    setLoading(true); // Set loading to true while API call is in progress

    try {
      // API call to sign in
      const response = await axios.post(`${MAIN_URL}admin/login`, {
        email,
        password,
      });

      // If login is successful, store token in localStorage and navigate
      if (response.status === 200) {
        // Store token in localStorage (or sessionStorage)
        localStorage.setItem('auth_token', response.data.auth_token);

        // Redirect the user
        navigate("/patients");
      }
    } catch (error) {
      // Handle API error response
      console.error("Login failed", error?.response?.data);
      setEmailError(true);
      setEmailErrorMessage("Invalid email or password.");
    } finally {
      setLoading(false); // Set loading to false after the API call is done
    }
  };


  return (
    <Card variant="outlined">
      <Box sx={{ display: { xs: "flex", md: "none" }, justifyContent: "center", mb: 1 }}>
        <Avatar
          src={LogoImage}
          alt="AB's Diet Planet"
          sx={{ width: 56, height: 56, borderRadius: 2 }}
        />
      </Box>

      <Box>
        <Typography
          component="h1"
          variant="h4"
          sx={{ fontWeight: 700, fontSize: "clamp(1.75rem, 8vw, 2.15rem)" }}
        >
          Welcome back
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Sign in to manage your patients and diet plans
        </Typography>
      </Box>

      <Box
        component="form"
        onSubmit={handleSubmit}
        noValidate
        sx={{ display: "flex", flexDirection: "column", width: "100%", gap: 2.5 }}
      >
        <TextField
          error={emailError}
          helperText={emailErrorMessage}
          id="email"
          type="email"
          name="email"
          label="Email"
          placeholder="your@email.com"
          autoComplete="email"
          autoFocus
          required
          fullWidth
          variant="outlined"
          color={emailError ? "error" : "primary"}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <EmailRounded fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          error={passwordError}
          helperText={passwordErrorMessage}
          name="password"
          label="Password"
          placeholder="••••••"
          type={showPassword ? 'text' : 'password'}
          id="password"
          autoComplete="current-password"
          required
          fullWidth
          variant="outlined"
          color={passwordError ? "error" : "primary"}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockRounded fontSize="small" color="action" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                  size="small"
                >
                  {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <FormControlLabel
          control={<Checkbox value="remember" color="primary" />}
          label="Remember me"
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <LoginRounded />}
          sx={{ borderRadius: 2, py: 1.2, textTransform: 'none', fontWeight: 600 }}
        >
          {loading ? "Signing in..." : "Sign in"}
        </Button>
      </Box>
    </Card>
  );
}
